import React, { useEffect, useMemo, useState } from 'react';
import { Search, Download, X, Loader2, Filter, Trash2 } from 'lucide-react';
import { adminApi } from './AdminAuthContext';

const STATUS_OPTIONS = [
  { id: '',          label: 'Alle Status' },
  { id: 'new',       label: 'Neu' },
  { id: 'contacted', label: 'Kontaktiert' },
  { id: 'closed',    label: 'Abgeschlossen' },
  { id: 'lost',      label: 'Verloren' },
];
const STATUS_COLORS = {
  new:       { bg: '#F26B1A', fg: '#fff', label: 'Neu' },
  contacted: { bg: '#E0A930', fg: '#1F1D1A', label: 'Kontaktiert' },
  closed:    { bg: '#7FC242', fg: '#1F1D1A', label: 'Abgeschlossen' },
  lost:      { bg: '#5C5852', fg: '#fff', label: 'Verloren' },
};

const LANG_OPTIONS = [
  { id: '',   label: 'Alle Sprachen' },
  { id: 'nl', label: 'Niederländisch' },
  { id: 'de', label: 'Deutsch' },
];

function StatusPill({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.new;
  return (
    <span className="inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-wider"
      style={{ backgroundColor: c.bg, color: c.fg }}>{c.label}</span>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

export default function AdminLeadsPage() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [language, setLanguage] = useState('');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (language) params.language = language;
      if (q.trim()) params.q = q.trim();
      const [{ data: list }, { data: s }] = await Promise.all([
        adminApi.get('/admin/leads', { params }),
        adminApi.get('/admin/leads/stats'),
      ]);
      setLeads(list);
      setStats(s);
      // Drop any selected IDs that are no longer in the result set (e.g.
      // after filter change) so the bulk-action bar reflects reality.
      setSelectedIds((prev) => {
        const visible = new Set(list.map((l) => l.id));
        const next = new Set();
        prev.forEach((id) => { if (visible.has(id)) next.add(id); });
        return next;
      });
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchLeads(); }, [status, language]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadCsv = async () => {
    const { data } = await adminApi.get('/admin/leads/export.csv', { responseType: 'blob' });
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url; a.download = 'horrenmaker_leads.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const onSubmitSearch = (e) => { e.preventDefault(); fetchLeads(); };

  const updateLead = async (id, patch) => {
    const { data } = await adminApi.patch(`/admin/leads/${id}`, patch);
    setLeads((arr) => arr.map((l) => (l.id === id ? data : l)));
    setSelected(data);
  };

  const deleteLead = async (id) => {
    if (!window.confirm('Diesen Lead unwiderruflich löschen?')) return;
    try {
      await adminApi.delete(`/admin/leads/${id}`);
      setLeads((arr) => arr.filter((l) => l.id !== id));
      setSelectedIds((s) => { const n = new Set(s); n.delete(id); return n; });
      setSelected(null);
      fetchLeads(); // refresh stats
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'Löschen fehlgeschlagen.');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === leads.length) return new Set();
      return new Set(leads.map((l) => l.id));
    });
  };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`${ids.length} Lead${ids.length === 1 ? '' : 's'} unwiderruflich löschen?`)) return;
    setBulkBusy(true);
    try {
      const { data } = await adminApi.post('/admin/leads/bulk-delete', { ids });
      setSelectedIds(new Set());
      await fetchLeads();
      window.alert(`${data.deleted_count} Lead${data.deleted_count === 1 ? '' : 's'} gelöscht.`);
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'Bulk-Löschen fehlgeschlagen.');
    } finally { setBulkBusy(false); }
  };

  const statCards = useMemo(() => {
    if (!stats) return [];
    const s = stats.by_status || {};
    return [
      { label: 'Gesamt',         n: stats.total || 0 },
      { label: 'Neu',            n: s.new || 0 },
      { label: 'Kontakt.',       n: s.contacted || 0 },
      { label: 'Gewonnen',       n: s.closed || 0 },
    ];
  }, [stats]);

  return (
    <div data-testid="admin-leads-page" className="w-full max-w-none">
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
        <h1 className="font-display text-[24px] sm:text-[28px] lg:text-[34px] text-[#1F1D1A] leading-none">Leads</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={downloadCsv} data-testid="admin-leads-csv"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E5E0D2] text-[12px] font-semibold text-[#1F1D1A] hover:border-[#F26B1A] hover:text-[#F26B1A] transition-colors">
            <Download className="w-3.5 h-3.5" /> CSV exportieren
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-6">
        {statCards.map((c) => (
          <div key={c.label} data-testid={`admin-stat-${c.label.toLowerCase()}`} className="bg-white rounded-xl border border-[#E5E0D2] p-3 sm:p-4 min-w-0">
            <p className="text-[10px] sm:text-[10.5px] uppercase tracking-[0.12em] sm:tracking-[0.16em] font-semibold text-[#5C5852] truncate">{c.label}</p>
            <p className="font-display text-[22px] sm:text-[28px] leading-none mt-2 tabular-nums">{c.n}</p>
          </div>
        ))}
      </div>

      <form onSubmit={onSubmitSearch} data-testid="admin-leads-filter" className="bg-white rounded-xl border border-[#E5E0D2] p-3 mb-4 flex flex-wrap gap-2 items-center">
        <Filter className="hidden sm:block w-4 h-4 text-[#5C5852] ml-1" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-leads-filter-status"
          className="flex-1 sm:flex-none min-w-0 px-3 py-1.5 rounded-md border border-[#E5E0D2] text-[13px] bg-white">
          {STATUS_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
        <select value={language} onChange={(e) => setLanguage(e.target.value)} data-testid="admin-leads-filter-lang"
          className="flex-1 sm:flex-none min-w-0 px-3 py-1.5 rounded-md border border-[#E5E0D2] text-[13px] bg-white">
          {LANG_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
        <div className="relative w-full sm:flex-1 sm:min-w-[200px] sm:w-auto">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5852]" />
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-leads-search"
            placeholder="Name, E-Mail oder Nachricht …"
            className="w-full pl-9 pr-3 py-1.5 rounded-md border border-[#E5E0D2] text-[13px]" />
        </div>
        <button type="submit" className="w-full sm:w-auto px-3 py-1.5 rounded-md bg-[#F26B1A] text-white text-[12px] font-semibold">Suchen</button>
      </form>

      {selectedIds.size > 0 && (
        <div data-testid="admin-leads-bulk-bar"
          className="bg-[#1F1D1A] text-white rounded-xl px-4 py-2.5 mb-3 flex items-center justify-between gap-3 flex-wrap">
          <span data-testid="admin-leads-bulk-count" className="text-[12.5px]">
            <strong className="tabular-nums">{selectedIds.size}</strong> ausgewählt
          </span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setSelectedIds(new Set())}
              data-testid="admin-leads-bulk-clear"
              className="px-2.5 py-1 rounded-full text-[11.5px] text-white/70 hover:text-white">Auswahl aufheben</button>
            <button type="button" onClick={bulkDelete} disabled={bulkBusy}
              data-testid="admin-leads-bulk-delete"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#C03A1A] text-white text-[12px] font-semibold hover:bg-[#A02E10] disabled:opacity-40 transition-colors">
              {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              {selectedIds.size === 1 ? '1 Lead löschen' : `${selectedIds.size} Leads löschen`}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-[#E5E0D2] overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#5C5852]"><Loader2 className="inline w-5 h-5 animate-spin" /></div>
        ) : leads.length === 0 ? (
          <p className="p-10 text-center text-[14px] text-[#5C5852]">Keine Leads gefunden.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] min-w-[680px]" data-testid="admin-leads-table">
            <thead className="bg-[#FAF8F2] border-b border-[#E5E0D2] text-[11px] uppercase tracking-wider text-[#5C5852]">
              <tr>
                <th className="text-left p-3 font-semibold w-10">
                  <input type="checkbox"
                    data-testid="admin-leads-select-all"
                    checked={leads.length > 0 && selectedIds.size === leads.length}
                    ref={(el) => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < leads.length; }}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 accent-[#F26B1A] cursor-pointer" />
                </th>
                <th className="text-left p-3 font-semibold">Datum</th>
                <th className="text-left p-3 font-semibold">Name</th>
                <th className="text-left p-3 font-semibold">E-Mail</th>
                <th className="text-left p-3 font-semibold">Quelle</th>
                <th className="text-left p-3 font-semibold">Sprache</th>
                <th className="text-left p-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => {
                const isSel = selectedIds.has(l.id);
                return (
                  <tr key={l.id} data-testid={`admin-lead-row-${l.id}`} onClick={() => setSelected(l)}
                    className={`border-b border-[#E5E0D2] hover:bg-[#FAF8F2] cursor-pointer transition-colors last:border-b-0 ${isSel ? 'bg-[#F26B1A]/5' : ''}`}>
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={isSel}
                        data-testid={`admin-lead-select-${l.id}`}
                        onChange={() => toggleSelect(l.id)}
                        className="w-4 h-4 accent-[#F26B1A] cursor-pointer" />
                    </td>
                    <td className="p-3 tabular-nums text-[#5C5852] whitespace-nowrap">{formatDate(l.created_at)}</td>
                    <td className="p-3 font-semibold">{l.name}</td>
                    <td className="p-3 text-[#5C5852]">{l.email}</td>
                    <td className="p-3 text-[#5C5852] text-[12px]">{l.source}</td>
                    <td className="p-3 text-[#5C5852] uppercase text-[11px]">{l.language}</td>
                    <td className="p-3"><StatusPill status={l.status || 'new'} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-lead-detail"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-[#E5E0D2]">
              <div>
                <p className="font-display text-[20px]">{selected.name}</p>
                <p className="text-[12px] text-[#5C5852]">{formatDate(selected.created_at)}</p>
              </div>
              <button type="button" onClick={() => setSelected(null)} data-testid="admin-lead-detail-close"
                className="w-8 h-8 rounded-full hover:bg-[#FAF8F2] inline-flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[13px]">
                <div><p className="text-[10.5px] uppercase tracking-wider text-[#5C5852]">E-Mail</p><p className="font-semibold">{selected.email}</p></div>
                <div><p className="text-[10.5px] uppercase tracking-wider text-[#5C5852]">Telefon</p><p className="font-semibold">{selected.phone || '—'}</p></div>
                <div><p className="text-[10.5px] uppercase tracking-wider text-[#5C5852]">Sprache</p><p className="font-semibold uppercase">{selected.language}</p></div>
                <div><p className="text-[10.5px] uppercase tracking-wider text-[#5C5852]">Quelle</p><p className="font-semibold">{selected.source}</p></div>
                <div><p className="text-[10.5px] uppercase tracking-wider text-[#5C5852]">Produkt</p><p className="font-semibold">{selected.product || '—'}</p></div>
                <div>
                  <p className="text-[10.5px] uppercase tracking-wider text-[#5C5852] mb-1">Status</p>
                  <select value={selected.status || 'new'} onChange={(e) => updateLead(selected.id, { status: e.target.value })}
                    data-testid="admin-lead-detail-status"
                    className="px-2 py-1 rounded border border-[#E5E0D2] text-[13px] bg-white">
                    {STATUS_OPTIONS.filter((o) => o.id).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              {selected.message && (
                <div>
                  <p className="text-[10.5px] uppercase tracking-wider text-[#5C5852] mb-1">Nachricht / Konfiguration</p>
                  <pre className="bg-[#FAF8F2] rounded-lg p-3 text-[12px] whitespace-pre-wrap font-mono">{selected.message}</pre>
                </div>
              )}
              <div>
                <p className="text-[10.5px] uppercase tracking-wider text-[#5C5852] mb-1">Interne Notiz</p>
                <textarea defaultValue={selected.admin_notes || ''} rows={3}
                  data-testid="admin-lead-detail-notes"
                  onBlur={(e) => { if ((e.target.value || '') !== (selected.admin_notes || '')) updateLead(selected.id, { admin_notes: e.target.value }); }}
                  placeholder="z. B. Termin 15.3. 14 Uhr"
                  className="w-full px-3 py-2 rounded-lg border border-[#E5E0D2] text-[13px] resize-none" />
                <p className="text-[10.5px] text-[#5C5852] mt-1">Wird automatisch gespeichert beim Verlassen des Feldes.</p>
              </div>
              <div className="pt-3 border-t border-[#E5E0D2] flex items-center justify-between">
                <p className="text-[11px] text-[#5C5852]">DSGVO: Lead-Daten können auf Wunsch des Kunden vollständig entfernt werden.</p>
                <button type="button" onClick={() => deleteLead(selected.id)}
                  data-testid="admin-lead-detail-delete"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#C03A1A]/40 text-[12px] font-semibold text-[#C03A1A] hover:bg-[#C03A1A] hover:text-white transition-colors">
                  <Trash2 className="w-3.5 h-3.5" /> Lead löschen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
